import { Badge } from '../components/primitives'
import { radius } from '../components/primitives/shape'

export type Tab = 'home' | 'direct' | 'activity' | 'settings'

const ITEMS: ReadonlyArray<{ id: Tab; label: string }> = [
  { id: 'home', label: 'Home' },
  { id: 'direct', label: 'DMs' },
  { id: 'activity', label: 'Activity' },
  { id: 'settings', label: 'Settings' },
]

/**
 * A phone's navigation, below 900px, where the sidebar is a screen of its own.
 * It floats rather than docking, and opening a room hides it: the room's
 * header has the way back.
 */
export function BottomNav({
  tab, unreadPlaces, waitingAgents, onSelect,
}: {
  tab: Tab
  /** Rooms and direct messages with something unread. */
  unreadPlaces: number
  waitingAgents: number
  onSelect: (tab: Tab) => void
}) {
  const badges: Partial<Record<Tab, { count: number; label: string }>> = {
    home: { count: unreadPlaces, label: `${unreadPlaces} ${unreadPlaces === 1 ? 'place' : 'places'} unread` },
    direct: { count: waitingAgents, label: `${waitingAgents} waiting on you` },
  }
  return (
    <nav
      aria-label="Sections"
      style={{
        position: 'absolute', left: 0, right: 0, bottom: 0,
        // The bar clears the home indicator rather than sitting under it. 18px
        // is the gap the design wants on a phone with a physical button; where
        // there is a gesture bar the inset is the gap, and it is already
        // larger. `max` rather than a sum so the bar does not float half a
        // thumb up the screen on the devices that have both.
        padding: '6px 16px max(18px, var(--safe-bottom))',
        display: 'flex', justifyContent: 'center',
        pointerEvents: 'none', zIndex: 10,
      }}
    >
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 2,
          background: 'var(--panel-strong)', border: '1px solid var(--border-strong)',
          borderRadius: radius.xl, padding: 5,
          backdropFilter: 'blur(24px)', boxShadow: '0 10px 34px rgba(0,0,0,.6)',
          pointerEvents: 'auto',
        }}
      >
        {ITEMS.map((item) => {
          const on = item.id === tab
          const badge = badges[item.id]
          return (
            <button
              key={item.id}
              onClick={() => onSelect(item.id)}
              aria-current={on}
              className="sp-bottom-nav-item"
              style={{
                position: 'relative', display: 'flex', flexDirection: 'column',
                alignItems: 'center', gap: 4, minWidth: 56,
                borderRadius: radius.lg, cursor: 'pointer',
                background: on ? 'var(--panel-hover)' : 'transparent',
              }}
            >
              <span
                style={{
                  width: 16, height: 16, borderRadius: '50%',
                  background: on ? 'var(--text)' : 'transparent',
                  border: on ? 'none' : '1.6px solid var(--text-meta)',
                }}
              />
              <span style={{ fontSize: 10, fontWeight: on ? 700 : 500, color: on ? 'var(--text)' : 'var(--text-dim)' }}>
                {item.label}
              </span>
              {badge && badge.count > 0 && (
                <span style={{ position: 'absolute', top: 2, left: 'calc(50% + 2px)' }}>
                  <Badge count={badge.count} label={badge.label} />
                </span>
              )}
            </button>
          )
        })}
      </div>
    </nav>
  )
}
